import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import './unifiedEditProfileModal.css';
import { showError } from '../../../shared/utils/swalHandler';
import {
  hasBlockingRestrictedContent,
  getRestrictedRanges,
  getProhibitWords,
  getLinkWords,
} from '../../../shared/utils/restrictedWordsUtils';
import { fetchRestrictedWords } from '../../../shared/actions/matrixAction';
import {
  ROLE,
  SUBSCRIPTION_PLAN,
  LIMIT_TYPE,
  CALL_PRICING_MODEL_PER_MINUTE,
  CALL_PRICING_MODEL_FLAT_RATE
} from '../../../shared/constants/common';
import {
  subsString,
  getFirstErrorMessage
} from '../../../shared/utils/function';
import { formatFreeTrialDurationText, getPromotionRemainingText } from '../../../shared/utils/paymentUtil';
import { createEditProfileSchema, normalizeMinCallMinutes } from '../../../shared/validations/editProfile';
import {
  fetchCreator,
  updateCreator,
  updateSubscriptionData
} from '../../../shared/actions/creatorActions';
import { fetchUser } from '../../../shared/actions/userActions';
import { createPromotion, cancelPromotion } from '../../../shared/actions/promotionAction';
import {
  showProfileUpdatedPushToast,
  showPromotionActivePushToast,
  showPromotionCancelledPushToast
} from '../../../shared/pusNotification/useManualToasts';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../../../shared/components/form';
import CallSettingsSection from './CallSettingsSection';
import { useModalOpen } from '../../../shared/utils/modalUtils.js';
import throneColoredLogo from '../../../assets/img/throne_colored.png';
import { reportError } from '../../../shared/sentry';

/** Returns unique restricted phrases found in text (so we can show which words to remove). */
function getRestrictedPhrasesFound(text, restrictedWords) {
  const ranges = getRestrictedRanges(text, restrictedWords);
  const seen = new Set();
  const phrases = [];
  for (const { start, end } of ranges) {
    const phrase = text.slice(start, end).trim();
    const key = phrase.toLowerCase();
    if (phrase && !seen.has(key)) {
      seen.add(key);
      phrases.push(phrase);
    }
  }
  return phrases;
}

/** Returns unique blocking (prohibit + link) phrases found in text — same condition as ChatComposer. */
function getBlockingRestrictedPhrasesFound(text, restrictedWords) {
  if (!Array.isArray(restrictedWords) || restrictedWords.length === 0) return [];
  const blockingWords = [...getProhibitWords(restrictedWords), ...getLinkWords(restrictedWords)];
  if (blockingWords.length === 0) return [];
  return getRestrictedPhrasesFound(text, blockingWords);
}

const getDefaultCallSettings = (callSettings, defaultPricingModel) => {
  const defaults = {
    enabled: true,
    pricingModel: defaultPricingModel,
    perMinute: {
      amount: CALL_PRICING_MODEL_PER_MINUTE.AMOUNT,
      minCallMinutess: CALL_PRICING_MODEL_PER_MINUTE.DURATION,
    },
    flatRate: {
      amount: CALL_PRICING_MODEL_FLAT_RATE.AMOUNT,
      durationMinutes: CALL_PRICING_MODEL_FLAT_RATE.DURATION,
    },
    available: true,
  };

  if (!callSettings) return defaults;

  return {
    ...defaults,
    ...callSettings,
    perMinute: {
      ...defaults.perMinute,
      ...callSettings.perMinute,
    },
    flatRate: {
      ...defaults.flatRate,
      ...callSettings.flatRate,
    },
  };
};

const getFormDataDefaults = (creatorInfo) => ({
  pinMessage: creatorInfo?.pinMessage || '',
  wishlistUrl: creatorInfo?.wishlistUrl || '',
  throneWishlistUrl: creatorInfo?.throneWishlistUrl || '',
  profile_description: creatorInfo?.profile_description || '',
  voice: getDefaultCallSettings(creatorInfo?.voice, 'per_minute'),
  video: getDefaultCallSettings(creatorInfo?.video, 'flat_rate'),
});

const getNormalizedFreeTrialDays = (promotionData) => {
  const incomingOfferType = promotionData?.offer_type || 'first_month';
  if (incomingOfferType !== 'free_trial') return null;

  const rawFreeTrialDays = promotionData?.free_trial_duration_days
    || (promotionData?.free_trial_duration_seconds
      ? Math.ceil(promotionData.free_trial_duration_seconds / 86400)
      : null);

  if (!rawFreeTrialDays || rawFreeTrialDays <= 0) return 1;
  if (rawFreeTrialDays <= 30) return rawFreeTrialDays;

  const months = Math.ceil(rawFreeTrialDays / 30);
  return months * 30;
};

const getPromotionDefaults = (promotionData) => {
  const incomingOfferType = promotionData?.offer_type || 'first_month';
  const normalizedFreeTrialDays = getNormalizedFreeTrialDays(promotionData);
  const limitType = promotionData?.limit_type || 'no_limit';
  const offerLimit = promotionData?.offer_limit || null;
  const autoResetEnabled = (limitType === 'fixed' && offerLimit && offerLimit >= 1)
    ? (promotionData?.auto_reset_enabled || false)
    : false;

  return {
    applies_to: 'subscription',
    offer_type: incomingOfferType,
    subscriber_type: promotionData?.subscriber_type || 'new',
    offer_expiration_days: promotionData?.offer_expiration_days ?? null,
    free_trial_duration_days: incomingOfferType === 'free_trial' ? (normalizedFreeTrialDays ?? 1) : null,
    discount: incomingOfferType === 'first_month' ? (promotionData?.discount || null) : null,
    limit_type: limitType,
    offer_limit: offerLimit,
    auto_reset_enabled: autoResetEnabled,
    per_user_limit: promotionData?.per_user_limit ?? 1,
    max_redemptions: promotionData?.max_redemptions || null,
  };
};

const getEditProfileFormValues = (creatorInfo, promotionData, subscriptionPrice, showSubscriptionSections) => {
  const formData = getFormDataDefaults(creatorInfo);

  if (showSubscriptionSections) {
    return {
      formData,
      subscription: {
        subscriptionPrice,
      },
      promotion: getPromotionDefaults(promotionData),
    };
  }

  return { formData };
};

const UnifiedEditProfileModal = ({
  isOpen,
  onClose,
  userRole,
  creatorInfo,
  promotionData,
  onSave,
  onPromotionChange
}) => {
  const dispatch = useDispatch();
  const restrictedWords = useSelector((state) => state.matrix?.restrictedWords?.data) || [];
  const canEditProfile = userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;
  const subscriptionPrice = creatorInfo?.user_subsData?.real_price || creatorInfo?.subscriptionPrice || '';
  // Determine if we should show subscription/promotion sections (only for CREATOR_SUB)
  const showSubscriptionSections = userRole === ROLE.CREATOR_SUB;
  // Check if there is an active campaign
  const IsActiveCampaign = promotionData?._id && promotionData?.active;  //  && promotionData?.status === PROMOTION_STATUS.RUNNING
  // Check if subscription price should be disabled (when there's an active campaign)
  const isSubscriptionPriceDisabled = creatorInfo?.user_subsData?.real_price && IsActiveCampaign;

  const [loading, setLoading] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const isEditProfileUpdate = creatorInfo?.isEditProfileUpdate || false;
  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(isOpen);

  // Initialize form with validation schema
  const form = useForm({
    resolver: zodResolver(createEditProfileSchema(showSubscriptionSections)),
    defaultValues: getEditProfileFormValues(
      creatorInfo,
      promotionData,
      subscriptionPrice,
      showSubscriptionSections
    ),
    // onBlur avoids re-validating the full Zod schema on every keystroke (lags hard on mobile)
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // Clear error message when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setErrorMessage(null);
      return;
    }
  }, [isOpen]);

  // Clear prohibited-content error when bio/pin no longer contain blocked words.
  // Use watch(callback) so we do NOT re-render the whole modal on every keystroke.
  useEffect(() => {
    if (!errorMessage || !restrictedWords?.length) return;
    const isBioError = errorMessage.includes('Bio contains prohibited words or links');
    const isPinError = errorMessage.includes('Pinned message contains prohibited words or links');
    if (!isBioError && !isPinError) return;

    const subscription = form.watch((value, { name }) => {
      if (
        name !== 'formData.profile_description' &&
        name !== 'formData.pinMessage'
      ) {
        return;
      }
      const bio = (value?.formData?.profile_description || '').trim();
      const pinMessage = (value?.formData?.pinMessage || '').trim();
      if (isBioError && !hasBlockingRestrictedContent(bio, restrictedWords)) {
        setErrorMessage(null);
        return;
      }
      if (isPinError && !hasBlockingRestrictedContent(pinMessage, restrictedWords)) {
        setErrorMessage(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [errorMessage, restrictedWords, form]);

  // Fetch restricted words when edit profile modal opens (same list as chat)
  useEffect(() => {
    if (isOpen && (!restrictedWords || restrictedWords.length === 0)) {
      dispatch(fetchRestrictedWords());
    }
  }, [isOpen, dispatch, restrictedWords]);

  // When modal opens, reset form to saved profile values (discards unsaved edits)
  useEffect(() => {
    if (!isOpen) return;

    form.reset(getEditProfileFormValues(
      creatorInfo,
      promotionData,
      subscriptionPrice,
      showSubscriptionSections
    ));
  }, [isOpen, promotionData, creatorInfo, showSubscriptionSections, subscriptionPrice, form]);

  const handleCancel = () => {
    form.reset(getEditProfileFormValues(
      creatorInfo,
      promotionData,
      subscriptionPrice,
      showSubscriptionSections
    ));
    form.clearErrors();
    setErrorMessage(null);
    onClose();
  };

  // Watch for changes in limit_type and offer_limit to automatically disable auto_reset_enabled if conditions aren't met
  const limitType = showSubscriptionSections ? form.watch('promotion.limit_type') : null;
  const offerLimit = showSubscriptionSections ? form.watch('promotion.offer_limit') : null;
  const autoResetEnabled = showSubscriptionSections ? form.watch('promotion.auto_reset_enabled') : false;

  useEffect(() => {
    if (!isOpen || !showSubscriptionSections) return;

    // If auto_reset_enabled is true but conditions aren't met, turn it off
    if (autoResetEnabled && (limitType === 'no_limit' || !offerLimit || offerLimit < 1)) {
      form.setValue('promotion.auto_reset_enabled', false);
    }
  }, [isOpen, showSubscriptionSections, limitType, offerLimit, autoResetEnabled]);

  const promotionRemainingText = useMemo(
    () => getPromotionRemainingText(promotionData),
    [promotionData]
  );

  const handleStartCampaign = async () => {
    try {
      setCampaignLoading(true);

      const promotionFormData = form.getValues('promotion');
      const subscriptionFormData = form.getValues('subscription');

      // Validate that subscription price is required for creating a plan
      if (!subscriptionFormData?.subscriptionPrice || subscriptionFormData.subscriptionPrice.trim() === '') {
        throw new Error('Subscription price is required to create a promotion plan');
      }

      // Update subscription price first (if changed and valid)
      let resultSubscription = null;
      if (subscriptionFormData?.subscriptionPrice &&
        subscriptionFormData.subscriptionPrice.trim() !== '' &&
        subscriptionFormData.subscriptionPrice.trim() !== subscriptionPrice.trim()) {
        const price = parseFloat(subscriptionFormData.subscriptionPrice.trim());

        // Validate that price is greater than 0
        if (price <= 0) {
          throw new Error('Subscription price must be greater than 0');
        }

        resultSubscription = await updateSubscriptionData({
          price: subscriptionFormData.subscriptionPrice.trim()
        });

        if (!resultSubscription?.success) {
          throw new Error(resultSubscription?.error || 'Failed to update subscription price');
        }
      }

      // Clean up the promotion data based on offer_type
      const cleanedPromotionData = {
        applies_to: promotionFormData.applies_to,
        offer_type: promotionFormData.offer_type,
        subscriber_type: promotionFormData.subscriber_type,
        limit_type: promotionFormData.limit_type,
        per_user_limit: promotionFormData.per_user_limit,
      };

      // Add conditional fields based on values
      if (promotionFormData.offer_expiration_days !== null) {
        cleanedPromotionData.offer_expiration_days = promotionFormData.offer_expiration_days;
      }

      if (promotionFormData.limit_type === 'fixed' && promotionFormData.offer_limit) {
        cleanedPromotionData.offer_limit = promotionFormData.offer_limit;
      }

      if (promotionFormData.auto_reset_enabled) {
        cleanedPromotionData.auto_reset_enabled = promotionFormData.auto_reset_enabled;
      }

      // Add offer-type specific fields
      if (promotionFormData.offer_type === 'free_trial') {
        if (promotionFormData.free_trial_duration_days) {
          cleanedPromotionData.free_trial_duration_days = promotionFormData.free_trial_duration_days;
        }
      } else if (promotionFormData.offer_type === 'first_month') {
        // Require a positive discount for first_month offers
        if (!promotionFormData.discount || !promotionFormData.discount.value || promotionFormData.discount.value <= 0) {
          throw new Error('Please select a discount');
        }
        cleanedPromotionData.discount = promotionFormData.discount;
      }

      // Call the promotion actions
      let response;
      response = await createPromotion(cleanedPromotionData);
      if (response.success) {
        // Refetch creator data if subscription was updated
        if (resultSubscription?.success) {
          dispatch(fetchCreator(creatorInfo?.username));
        }

        // Call parent callback to refetch promotion data
        if (onPromotionChange) {
          await onPromotionChange();
        }
        setErrorMessage(null);
        showPromotionActivePushToast(creatorInfo);
        // Close modal
        onClose()
      } else {
        throw new Error(response.error || 'Failed to start campaign');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to start campaign. Please try again.');
    } finally {
      setCampaignLoading(false);
    }
  };

  const handleCancelCampaign = async () => {
    try {
      setCampaignLoading(true);

      const promotionId = promotionData?._id;
      if (!promotionId) {
        throw new Error('No active campaign to cancel');
      }

      // Call the cancel promotion action
      const response = await cancelPromotion(promotionId);
      if (response.success) {
        // Call parent callback to refetch promotion data
        if (onPromotionChange) {
          await onPromotionChange();
        }
        // Reset promotion form fields to default values after cancel
        form.reset({
          ...form.getValues(),
          promotion: {
            applies_to: 'subscription',
            offer_type: 'first_month',
            subscriber_type: 'new',
            offer_expiration_days: null,
            free_trial_duration_days: 1,
            discount: null,
            limit_type: 'no_limit',
            offer_limit: null,
            auto_reset_enabled: false,
            per_user_limit: 1,
            max_redemptions: null,
          },
        });
        setErrorMessage(null);
        showPromotionCancelledPushToast(creatorInfo);
      } else {
        throw new Error(response.error || 'Failed to cancel campaign');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to cancel campaign. Please try again.');
    } finally {
      setCampaignLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Transform form data to add minCallSeconds and durationSeconds
      const voiceMinCallMinutes = normalizeMinCallMinutes(
        data.formData.voice?.perMinute?.minCallMinutess
      );
      const videoMinCallMinutes = normalizeMinCallMinutes(
        data.formData.video?.perMinute?.minCallMinutess
      );

      const transformedFormData = {
        ...data.formData,
        isEditProfileUpdate: true,
        voice: data.formData.voice ? {
          ...data.formData.voice,
          perMinute: data.formData.voice.perMinute ? {
            ...data.formData.voice.perMinute,
            minCallMinutess: voiceMinCallMinutes,
            minCallSeconds: voiceMinCallMinutes * 60,
          } : data.formData.voice.perMinute,
          flatRate: data.formData.voice.flatRate ? {
            ...data.formData.voice.flatRate,
            ...(typeof data.formData.voice.flatRate.durationMinutes === 'number' && {
              durationSeconds: data.formData.voice.flatRate.durationMinutes * 60
            })
          } : data.formData.voice.flatRate
        } : data.formData.voice,
        video: data.formData.video ? {
          ...data.formData.video,
          perMinute: data.formData.video.perMinute ? {
            ...data.formData.video.perMinute,
            minCallMinutess: videoMinCallMinutes,
            minCallSeconds: videoMinCallMinutes * 60,
          } : data.formData.video.perMinute,
          flatRate: data.formData.video.flatRate ? {
            ...data.formData.video.flatRate,
            ...(typeof data.formData.video.flatRate.durationMinutes === 'number' && {
              durationSeconds: data.formData.video.flatRate.durationMinutes * 60
            })
          } : data.formData.video.flatRate
        } : data.formData.video
      };

      // Call updateCreator directly without dispatch
      const result = await updateCreator(transformedFormData);

      // API 2: Update subscription price (only for CREATOR_SUB and if price is provided)
      let resultSubscription = null;
      if (showSubscriptionSections &&
        data.subscription?.subscriptionPrice &&
        !IsActiveCampaign &&
        data.subscription?.subscriptionPrice.trim() !== subscriptionPrice.trim()) {
        const price = data.subscription.subscriptionPrice;
        if (price.trim() !== '') {
          resultSubscription = await updateSubscriptionData({ price: price.trim() });
        }
      }

      // Check success: both results if subscription was updated, otherwise just result
      const isSuccess = showSubscriptionSections &&
        data.subscription?.subscriptionPrice &&
        !IsActiveCampaign &&
        data.subscription?.subscriptionPrice.trim() !== subscriptionPrice.trim()
        ? (result.success && resultSubscription?.success)
        : result.success;

      if (isSuccess) {
        setLoading(false);

        // Refresh public creator profile and logged-in user (call rates live on state.user.user).
        dispatch(fetchCreator(creatorInfo?.username));
        dispatch(fetchUser());

        setErrorMessage(null);
        showProfileUpdatedPushToast(creatorInfo);

        // Trigger onSave callback
        if (onSave) {
          onSave(transformedFormData);
        }
      } else {
        setLoading(false);
        const errorMsg = resultSubscription && !resultSubscription.success
          ? (resultSubscription.error || 'Failed to update subscription')
          : (result.error || 'Failed to update profile');
        throw new Error(errorMsg);
      }
    } catch (error) {
      reportError(error, 'UnifiedEditProfile update profile');
      setLoading(false);
      // Set error message to display under buttons
      setErrorMessage(error.message || 'Failed to update profile. Please try again.');
    }
  };

  // Handle save button click - skip promotion validation
  const handleSaveClick = async (e) => {
    e.preventDefault();

    // Clear promotion errors first to skip promotion validation
    form.clearErrors('promotion');

    // Validate only formData and subscription fields, excluding promotion
    const formDataValid = await form.trigger('formData');
    const subscriptionValid = showSubscriptionSections ? await form.trigger('subscription') : true;

    if (formDataValid && subscriptionValid) {
      // Get form values excluding promotion validation
      const formValues = form.getValues();
      const bio = (formValues.formData?.profile_description || '').trim();
      const pinMessage = (formValues.formData?.pinMessage || '').trim();

      // Block save if bio or pinned message contains prohibited words or links (same condition as ChatComposer)
      if (restrictedWords?.length) {
        if (hasBlockingRestrictedContent(bio, restrictedWords)) {
          const bioPhrases = getBlockingRestrictedPhrasesFound(bio, restrictedWords);
          const phraseList = bioPhrases.length ? bioPhrases.map((p) => `"${p}"`).join(', ') : '';
          const message = phraseList
            ? `Your bio contains prohibited words or links: ${phraseList}. Please change or remove them before saving.`
            : 'Your bio contains prohibited words or links. Please change or remove them before saving.';
          await showError('Prohibited content', message);
          setErrorMessage(`Bio contains prohibited words or links${phraseList ? `: ${phraseList}` : ''}. Please change or remove them before saving.`);
          return;
        }
        if (hasBlockingRestrictedContent(pinMessage, restrictedWords)) {
          const pinPhrases = getBlockingRestrictedPhrasesFound(pinMessage, restrictedWords);
          const phraseList = pinPhrases.length ? pinPhrases.map((p) => `"${p}"`).join(', ') : '';
          const message = phraseList
            ? `Your pinned message contains prohibited words or links: ${phraseList}. Please change or remove them before saving.`
            : 'Your pinned message contains prohibited words or links. Please change or remove them before saving.';
          await showError('Prohibited content', message);
          setErrorMessage(`Pinned message contains prohibited words or links${phraseList ? `: ${phraseList}` : ''}. Please change or remove them before saving.`);
          return;
        }
      }

      await onSubmit(formValues);
    } else {
      // Get the first error message
      const errors = form.formState.errors;
      const firstErrorMessage = getFirstErrorMessage(errors);

      // Set error message to display under buttons
      if (firstErrorMessage) {
        setErrorMessage(firstErrorMessage);
      } else {
        setErrorMessage('Please fix the errors in the form');
      }

      // Focus on first error field
      if (errors.formData) {
        // Find first error in formData (including nested paths)
        const findFirstErrorPath = (obj, prefix = '') => {
          for (const key in obj) {
            const path = prefix ? `${prefix}.${key}` : key;
            if (obj[key]?.message) {
              return path;
            }
            if (typeof obj[key] === 'object' && obj[key] !== null) {
              const nestedPath = findFirstErrorPath(obj[key], path);
              if (nestedPath) return nestedPath;
            }
          }
          return null;
        };
        const firstErrorPath = findFirstErrorPath(errors.formData, 'formData');
        if (firstErrorPath) {
          form.setFocus(firstErrorPath);
        }
      } else if (errors.subscription) {
        form.setFocus('subscription.subscriptionPrice');
      }
    }
  };

  // Don't render if not open or user doesn't have permission
  if (!isOpen || !canEditProfile) {
    return null;
  }

  return createPortal(
    <div className="unified-edit-profile-overlay cm_bg_blur">
      <div className="unified-edit-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="unified-edit-profile-content">
          <h2 className="unified-edit-profile-title">Edit Profile</h2>

          <Form {...form}>
            <form onSubmit={handleSaveClick}>
              <div className="unified-edit-profile-sections">
                {/* Subscription Price Section - Only for CREATOR_SUB (Role 1) */}
                {showSubscriptionSections && (
                  <div className="unified-edit-section">
                    <div className="unified-edit-section-header">
                      <i className="fa-solid fa-dollar-sign unified-edit-section-icon"></i>
                      <span className="unified-edit-section-title">Subscription</span>
                    </div>
                    {isSubscriptionPriceDisabled && (
                      <div className="unified-edit-price-cancel" role="alert">
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <p>Cancel your active campaign or wait for it to end before updating your subscription price.</p>
                      </div>
                    )}
                    <FormField
                      control={form.control}
                      name="subscription.subscriptionPrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="unified-edit-price-input-wrapper">
                              <span className="unified-edit-price-prefix">$</span>
                              <input
                                type="text"
                                className={`unified-edit-price-input ${form.formState.errors.subscription?.subscriptionPrice ? 'error' : ''}`}
                                placeholder={`Charge up to $${SUBSCRIPTION_PLAN.MAX_PRICE}`}
                                value={field.value || ''}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  // Only allow numbers and at most one decimal point
                                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                    // If there's a decimal point, limit to 2 decimal places
                                    if (value.includes('.')) {
                                      const [intPart, decPart] = value.split('.');
                                      if (decPart.length > 2) {
                                        value = `${intPart}.${decPart.slice(0, 2)}`;
                                      }
                                    }
                                    field.onChange(value);
                                  }
                                }}
                                onBlur={field.onBlur}
                                name={field.name}
                                disabled={isSubscriptionPriceDisabled}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {form.watch('subscription.subscriptionPrice') && (
                      <div className="unified-edit-price-info">
                        <p className="unified-edit-price-text">Set price: ${form.watch('subscription.subscriptionPrice')}/mo</p>
                      </div>
                    )}
                    <p className="unified-edit-price-note">You keep 100% of what you charge.</p>
                  </div>
                )}

                {/* Promotion Campaign Section - Only for CREATOR_SUB (Role 1) */}
                {showSubscriptionSections && (
                  <div className="unified-edit-section">
                    <div className="unified-edit-section-header">
                      <i className="fa-solid fa-gift unified-edit-section-icon"></i>
                      <span className="unified-edit-section-title">Promotion Campaign</span>
                    </div>
                    <p className="unified-edit-campaign-description">
                      Users will not be auto-renewed and will need to subscribe once their promotional offer ends.
                    </p>
                    {IsActiveCampaign && (() => {
                      // Calculate discounted price
                      const basePrice = parseFloat(subscriptionPrice.trim()) || parseFloat(SUBSCRIPTION_PLAN.DEFAULT_PRICE);
                      let discountedPrice;
                      let discountDisplay;

                      if (promotionData?.offer_type === 'free_trial') {
                        discountedPrice = 0;
                        const freeTrialDurationDays = promotionData.free_trial_duration_days
                          || (promotionData.free_trial_duration_seconds ? Math.round(promotionData.free_trial_duration_seconds / 86400) : 1);
                        discountDisplay = formatFreeTrialDurationText(freeTrialDurationDays);
                      } else if (promotionData?.discount?.type === 'percent') {
                        const discountAmount = (basePrice * promotionData.discount.value) / 100;
                        discountedPrice = basePrice - discountAmount;
                        discountDisplay = `${promotionData.discount.value}% Off`;
                      } else if (promotionData?.discount?.value) {
                        discountedPrice = basePrice - promotionData.discount.value;
                        discountDisplay = `$${promotionData.discount.value} Off`;
                      } else {
                        discountedPrice = basePrice;
                        discountDisplay = 'No discount';
                      }

                      const subscriberTypeText = promotionData?.subscriber_type === 'new'
                        ? 'new subscribers only'
                        : promotionData?.subscriber_type === 'expired'
                          ? 'expired subscribers only'
                          : 'new and expired subscribers';

                      return (
                        <div className="unified-edit-price-warning">
                          <div className="unified-edit-campaign-header">
                            Active Promotion Campaign
                            {promotionRemainingText && promotionRemainingText.isValid && promotionRemainingText.text !== '' &&
                              <span className="unified-edit-campaign-slots">
                                {promotionRemainingText.text}
                              </span>
                            }
                          </div>
                          <div className="unified-edit-campaign-content">
                            <p>
                              <span className='mr-2'>•</span>
                              {promotionData?.offer_type === 'free_trial'
                                ? `${discountDisplay}, then $${basePrice.toFixed(2)}/mo`
                                : `$${discountedPrice.toFixed(2)}/mo (${discountDisplay} from $${basePrice.toFixed(2)})`
                              } for {subscriberTypeText}
                            </p>

                            {promotionData.offer_remaining > 0 && promotionData.limit_type === LIMIT_TYPE.FIXED &&
                              <p>
                                <span className='mr-2'>•</span>
                                This offer is limited to {promotionData.offer_remaining} slots
                              </p>
                            }
                          </div>
                        </div>
                      );
                    })()}
                    <div className="unified-edit-campaign-options">
                      {/* Offer Type */}
                      <FormField
                        control={form.control}
                        name="promotion.offer_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="unified-edit-label">Offer type</FormLabel>
                            <div className="unified-edit-button-group">
                              <button
                                type="button"
                                className={`unified-edit-option-button ${field.value === 'first_month' ? 'active' : ''}`}
                                onClick={() => {
                                  field.onChange('first_month');
                                  // Clear free trial duration and ensure discount is null (user can set it)
                                  form.setValue('promotion.free_trial_duration_days', null);
                                  form.setValue('promotion.discount', null);
                                }}
                                disabled={IsActiveCampaign}
                              >
                                First month
                              </button>
                              <button
                                type="button"
                                className={`unified-edit-option-button ${field.value === 'free_trial' ? 'active' : ''}`}
                                onClick={() => {
                                  field.onChange('free_trial');
                                  // Clear discount and set default free trial duration
                                  form.setValue('promotion.discount', null);
                                  if (!form.getValues('promotion.free_trial_duration_days')) {
                                    form.setValue('promotion.free_trial_duration_days', 1);
                                  }
                                }}
                                disabled={IsActiveCampaign}
                              >
                                Free trial
                              </button>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {/* Offer Limit & Expiration */}
                      <div className="unified-edit-row">
                        <FormField
                          control={form.control}
                          name="promotion.limit_type"
                          render={({ field }) => {
                            const currentLimit = form.watch('promotion.offer_limit');
                            const displayValue = field.value === 'no_limit' ? 'no-limit' : String(currentLimit || '10');

                            return (
                              <FormItem className="unified-edit-form-group">
                                <FormLabel className="unified-edit-label">Offer limit</FormLabel>
                                <FormControl>
                                  <select
                                    className="unified-edit-select"
                                    value={displayValue}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === 'no-limit') {
                                        field.onChange('no_limit');
                                        form.setValue('promotion.offer_limit', null);
                                        // Reset auto_reset_enabled when limit is set to no_limit
                                        form.setValue('promotion.auto_reset_enabled', false);
                                      } else {
                                        field.onChange('fixed');
                                        const limitValue = parseInt(value);
                                        form.setValue('promotion.offer_limit', limitValue);
                                        // Reset auto_reset_enabled if limit is less than 1
                                        if (limitValue < 1) {
                                          form.setValue('promotion.auto_reset_enabled', false);
                                        }
                                      }
                                    }}
                                    disabled={IsActiveCampaign}
                                  >
                                    <option value="no-limit">No limit</option>
                                    {[
                                      ...Array.from({ length: 10 }, (_, i) => i + 1),
                                      ...Array.from({ length: 18 }, (_, i) => 15 + i * 5),
                                    ].map((value) => (
                                      <option value={value} key={value}>
                                        {value} sub{subsString(value)}
                                      </option>
                                    ))}
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                        <FormField
                          control={form.control}
                          name="promotion.offer_expiration_days"
                          render={({ field }) => {
                            const displayValue = field.value === null ? 'no-expiration' : String(field.value);

                            return (
                              <FormItem className="unified-edit-form-group mb-3">
                                <FormLabel className="unified-edit-label">Offer expiration</FormLabel>
                                <FormControl>
                                  <select
                                    className="unified-edit-select"
                                    value={displayValue}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      field.onChange(value === 'no-expiration' ? null : parseInt(value));
                                    }}
                                    disabled={IsActiveCampaign}
                                  >
                                    <option value="no-expiration">No expiration</option>
                                    {Array.from({ length: 30 }, (_, i) => (
                                      <option value={i + 1} key={i + 1}>
                                        {i + 1} day{subsString(i + 1)}
                                      </option>
                                    ))}
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>
                      {/* Discount & Reset */}
                      {/* Only show discount for first_month offer type */}
                      {form.watch('promotion.offer_type') === 'first_month' && (
                        <FormField
                          control={form.control}
                          name="promotion.discount"
                          render={({ field }) => {
                            const displayValue = field.value?.value ? String(field.value.value) : '0';

                            return (
                              <FormItem className="unified-edit-form-group mb-0">
                                <FormLabel className="unified-edit-label">
                                  Discount percentage
                                  <span className="modern-field-required">*</span>
                                </FormLabel>
                                <FormControl>
                                  <select
                                    className="unified-edit-select"
                                    value={displayValue}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value);
                                      field.onChange(value === 0 ? null : { type: 'percent', value });
                                    }}
                                    disabled={IsActiveCampaign}
                                  >
                                    <option value="0">Select discount</option>
                                    {Array.from({ length: 15 }, (_, i) => {
                                      const value = (i + 1) * 5;
                                      return (
                                        <option value={value} key={value}>
                                          {value}% discount
                                        </option>
                                      );
                                    })}
                                  </select>
                                </FormControl>
                              </FormItem>
                            );
                          }}
                        />
                      )}
                      {/* Show free trial duration for free_trial offer type */}
                      {form.watch('promotion.offer_type') === 'free_trial' && (
                        <FormField
                          control={form.control}
                          name="promotion.free_trial_duration_days"
                          render={({ field }) => {
                            // Expansion for free trial months
                            const MONTH_DAY_MAP = [2, 4, 6, 8, 10, 12].map((m) => ({ months: m, days: m * 30 }));
                            // Detect if the value is a monthly preset (multiple of 30, >= 60)
                            function getFreeTrialDisplayValue(val) {
                              if (!val || val <= 30) return String(val || "1");
                              // Map any value > 30 to the nearest month option using ceil
                              const months = Math.ceil(val / 30);
                              const snappedDays = months * 30;
                              const asMonth = MONTH_DAY_MAP.find(o => o.days === snappedDays);
                              return asMonth ? String(asMonth.days) : String(snappedDays);
                            }
                            return (
                              <FormItem className="unified-edit-form-group">
                                <FormLabel className="unified-edit-label">Free trial duration</FormLabel>
                                <FormControl>
                                  <select
                                    className="unified-edit-select"
                                    value={getFreeTrialDisplayValue(field.value)}
                                    onChange={(e) => {
                                      const v = parseInt(e.target.value);
                                      field.onChange(v);
                                    }}
                                    disabled={IsActiveCampaign}
                                  >
                                    <option value="1">1 day</option>
                                    <option value="3">3 days</option>
                                    <option value="7">7 days</option>
                                    <option value="14">14 days</option>
                                    <option value="30">30 days</option>
                                    {MONTH_DAY_MAP.map((opt) => (
                                      <option key={opt.months} value={opt.days}>
                                        {opt.months} month{opt.months > 1 ? 's' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      )}
                      <FormField
                        control={form.control}
                        name="promotion.auto_reset_enabled"
                        render={({ field }) => {
                          const currentLimit = form.watch('promotion.offer_limit');
                          const limitType = form.watch('promotion.limit_type');
                          // Toggle should be disabled if limit_type is 'no_limit' or offer_limit is less than 1
                          const isToggleDisabled = IsActiveCampaign || limitType === 'no_limit' || !currentLimit || currentLimit < 1;

                          return (
                            <FormItem className="unified-edit-toggle-group mb-0">
                              <div className="unified-edit-toggle-row">
                                <span className="unified-edit-toggle-label">Automatically reset offer limit</span>
                                <label className="unified-edit-toggle mb-0">
                                  <FormControl>
                                    <input
                                      type="checkbox"
                                      className="unified-edit-toggle-input"
                                      checked={field.value}
                                      onChange={(e) => {
                                        // Only allow toggle if conditions are met
                                        if (!isToggleDisabled) {
                                          field.onChange(e.target.checked);
                                        }
                                      }}
                                      disabled={isToggleDisabled}
                                    />
                                  </FormControl>
                                  <span className="unified-edit-toggle-slider"></span>
                                </label>
                              </div>
                              <p
                                className={`unified-edit-toggle-text ${!(currentLimit && currentLimit > 0) ? 'hidden' : ''}`}
                              >
                                Offer limit will reset back to {currentLimit || 10} once it reaches 0.
                              </p>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                      {/* Subscriber Type */}
                      <FormField
                        control={form.control}
                        name="promotion.subscriber_type"
                        render={({ field }) => (
                          <FormItem className="unified-edit-form-group mb-0">
                            <FormLabel className="unified-edit-label">Subscriber type</FormLabel>
                            <div className="unified-edit-subscriber-buttons">
                              <button
                                type="button"
                                className={`unified-edit-subscriber-button ${field.value === 'new' ? 'active' : ''}`}
                                onClick={() => field.onChange('new')}
                                disabled={IsActiveCampaign}
                              >
                                New subscribers
                              </button>
                              <button
                                type="button"
                                className={`unified-edit-subscriber-button ${field.value === 'expired' ? 'active' : ''}`}
                                onClick={() => field.onChange('expired')}
                                disabled={IsActiveCampaign}
                              >
                                Expired subscribers
                              </button>
                              <button
                                type="button"
                                className={`unified-edit-subscriber-button ${field.value === 'both' ? 'active' : ''}`}
                                onClick={() => field.onChange('both')}
                                disabled={IsActiveCampaign}
                              >
                                Both new and expired
                              </button>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="unified-edit-form-group">
                        {(() => {
                          const offerType = form.watch('promotion.offer_type');
                          const discount = form.watch('promotion.discount');
                          const freeTrialDuration = form.watch('promotion.free_trial_duration_days');
                          // Watch errors to ensure reactivity
                          const promotionErrors = form.formState.errors.promotion;

                          // Check if promotion form is valid
                          const isPromotionValid = (() => {
                            // If there are any errors in promotion, form is invalid
                            if (promotionErrors && Object.keys(promotionErrors).length > 0) return false;

                            // Validate based on offer type
                            if (offerType === 'first_month') {
                              // For first_month, discount is required and must be > 0
                              return discount && discount.value && discount.value > 0 && discount.value <= 100;
                            } else if (offerType === 'free_trial') {
                              // For free_trial, free_trial_duration_days is required and >= 1
                              return freeTrialDuration && typeof freeTrialDuration === 'number' && freeTrialDuration >= 1;
                            }
                            return false;
                          })();

                          // Button should be pink (active) when:
                          // 1. free_trial is selected (has default parameters)
                          // 2. first_month is selected and discount is chosen
                          const isButtonActive = offerType === 'free_trial' ||
                            (offerType === 'first_month' && discount && discount.value && discount.value > 0);
                          const buttonClassName = `unified-edit-campaign-button ${IsActiveCampaign ? 'cancel-button' : ''} ${!IsActiveCampaign && isButtonActive ? 'active-button' : ''}`;

                          return (
                            <button
                              type="button"
                              className={buttonClassName}
                              onClick={IsActiveCampaign ? handleCancelCampaign : handleStartCampaign}
                              disabled={campaignLoading || loading || !isPromotionValid}
                            >
                              {campaignLoading
                                ? (IsActiveCampaign ? 'Cancelling Campaign...' : 'Starting Campaign...')
                                : (IsActiveCampaign ? 'Cancel Campaign' : 'Start Campaign')
                              }
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Call Settings Section - Voice Call, Video Call, and Warning Banner */}
                <CallSettingsSection
                  control={form.control}
                  isEditProfileUpdate={isEditProfileUpdate}
                />

                {/* Bio Section - Common for both roles */}
                <div className="unified-edit-section">
                  <div className="unified-edit-section-header">
                    <i className="fa-solid fa-user unified-edit-section-icon"></i>
                    <span className="unified-edit-section-title">Bio</span>
                  </div>
                  <FormField
                    control={form.control}
                    name="formData.profile_description"
                    render={({ field }) => (
                      <FormItem>
                        <div className="unified-edit-textarea-container">
                          <FormControl>
                            <textarea
                              className="unified-edit-textarea"
                              rows="3"
                              placeholder="Let your audience know the type of content they can expect…"
                              maxLength={1000}
                              {...field}
                            />
                          </FormControl>
                          <div className="unified-edit-char-count">{field.value?.length || 0}/1000</div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Pinned Message Section - Common for both roles */}
                <div className="unified-edit-section">
                  <div className="unified-edit-section-header">
                    <i className="fa-solid fa-thumbtack unified-edit-section-icon"></i>
                    <span className="unified-edit-section-title">Pinned Message</span>
                  </div>
                  <FormField
                    control={form.control}
                    name="formData.pinMessage"
                    render={({ field }) => (
                      <FormItem>
                        <div className="unified-edit-textarea-container">
                          <FormControl>
                            <textarea
                              className="unified-edit-textarea"
                              rows="2"
                              placeholder="Write a message that will be pinned to the top of your profile..."
                              maxLength={150}
                              {...field}
                            />
                          </FormControl>
                          <div className="unified-edit-char-count">{field.value?.length || 0}/150</div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Amazon Wish List Section - Common for both roles */}
                <div className="unified-edit-section">
                  <div className="unified-edit-section-header">
                    <i className="fa-brands fa-amazon unified-edit-section-icon amazon-icon"></i>
                    <span className="unified-edit-section-title">Amazon Wish List</span>
                  </div>
                  <FormField
                    control={form.control}
                    name="formData.wishlistUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl className="mb-2">
                          <input
                            type="url"
                            className="unified-edit-url-input"
                            placeholder="https://amazon.com/wishlist/..."
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Throne wish list — optional alternative to Amazon (privacy-friendly gifting) */}
                <div className="unified-edit-section">
                  <div className="unified-edit-section-header">
                    <img
                      src={throneColoredLogo}
                      alt=""
                      className="unified-edit-section-throne-logo"
                    />
                    <span className="unified-edit-section-title">Throne Wish List</span>
                  </div>
                  <FormField
                    control={form.control}
                    name="formData.throneWishlistUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl className="mb-2">
                          <input
                            type="url"
                            className="unified-edit-url-input"
                            placeholder="https://throne.com/..."
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="unified-edit-profile-actions">
                <div className="unified-edit-actions-wrapper">
                  <div className="unified-edit-buttons-row">
                    <button
                      type="button"
                      className="unified-edit-cancel-btn"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="unified-edit-save-btn"
                      disabled={loading || form.formState.isSubmitting}
                    >
                      {loading || form.formState.isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  {errorMessage && (
                    <div className="unified-edit-error-message">
                      {errorMessage}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UnifiedEditProfileModal;

